import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, X, Loader2, MessageSquarePlus, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_BASE = `${window.location.origin}/api/ai`;

const AIAssistant = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: 'user', content: text, id: `u-${Date.now()}` };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await axios.post(
        `${API_BASE}/chat`,
        { session_id: sessionId, message: text },
        { headers: getAuthHeaders(), timeout: 90000 }
      );
      if (res.data?.session_id && !sessionId) setSessionId(res.data.session_id);
      const assistantMsg = {
        role: 'assistant',
        content: res.data?.message?.content || '(no reply)',
        id: res.data?.message?.id || `a-${Date.now()}`,
      };
      setMessages((m) => [...m, assistantMsg]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            err.response?.data?.detail ||
            'Sorry, I could not reach the AI service. Please try again.',
          id: `e-${Date.now()}`,
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  if (!user) return null;

  const suggestions = [
    'How many active AMCs do we have?',
    'Give me a summary of my open sales orders',
    'What is this ERP’s margin control module?',
    'Help me draft an executive update for this week',
  ];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          data-testid="ai-assistant-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
          aria-label="Open AI Assistant"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
            <Bot className="w-5 h-5" />
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-indigo-600" />
          </span>
          <span className="hidden sm:inline text-sm font-semibold">Ask AI</span>
        </button>
      )}

      {/* Chat drawer */}
      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-24px)] h-[600px] max-h-[calc(100vh-48px)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">Smarthub AI</div>
                <div className="text-[11px] text-white/80">
                  {sending ? 'Thinking…' : 'Business & Project Assistant'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="ai-new-chat"
                onClick={newChat}
                className="h-8 w-8 rounded-md hover:bg-white/15 flex items-center justify-center"
                title="New chat"
              >
                <MessageSquarePlus className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid="ai-close"
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-md hover:bg-white/15 flex items-center justify-center"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50"
          >
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="text-sm text-slate-700 leading-relaxed">
                  Hi {user.full_name?.split(' ')[0] || 'there'} <span role="img" aria-label="wave">👋</span>,
                  I’m your Enerzia Smarthub AI assistant. I can answer questions about
                  your projects, AMCs, sales orders, finance and ERP workflows.
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`ai-suggestion-${s.slice(0, 10)}`}
                      onClick={() => setInput(s)}
                      className="text-left text-xs rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-slate-700"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-sm'
                      : m.error
                      ? 'bg-rose-50 border border-rose-200 text-rose-700 rounded-bl-sm'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm shadow-sm px-3 py-2 text-sm text-slate-500 inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 bg-white p-2">
            <div className="flex items-end gap-2 rounded-xl border border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 px-2 py-1.5">
              <textarea
                ref={inputRef}
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
                placeholder="Ask about projects, AMCs, finance…"
                className="flex-1 resize-none outline-none text-sm bg-transparent max-h-32 py-1"
                disabled={sending}
              />
              <button
                type="button"
                data-testid="ai-send"
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="h-8 w-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="mt-1 text-[10px] text-slate-400 text-center">
              Powered by GPT-4o · Emergent Universal Key
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
